/**
 * SaxoBanner — surfaces a reconnect CTA when the Saxo OAuth chain is broken.
 *
 * Polls GET /api/saxo/status on mount and every 5 minutes thereafter.
 * The status route now reports both `connected` (token doc exists in Mongo)
 * and `healthy` (the token can actually authenticate to Saxo right now).
 * We show the banner whenever `healthy === false`, including the case where
 * the doc still exists but Saxo has invalidated the refresh chain server-side
 * (the failure mode that bit us on 09-Jun-2026).
 *
 * The banner includes the Saxo user ID to log in with (1015144) so there's
 * no friction looking it up before clicking through.
 */

import React, { useEffect, useState } from 'react';
import { apiGet } from '../../utils/api';

const POLL_INTERVAL_MS = 5 * 60 * 1000;

export default function SaxoBanner() {
  const [status, setStatus] = useState(null);
  const [loaded, setLoaded]  = useState(false);

  useEffect(() => {
    let alive = true;
    const fetchStatus = async () => {
      try {
        const r = await apiGet('/api/saxo/status');
        if (alive) {
          setStatus(r);
          setLoaded(true);
        }
      } catch (e) {
        // Treat network failure as "unknown" rather than broken — we don't want
        // to nag the user about Saxo when the whole API is down.
        if (alive) setLoaded(true);
      }
    };
    fetchStatus();
    const id = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // Hidden until we have a definitive "unhealthy" answer.
  if (!loaded || !status) return null;
  if (status.healthy)    return null;

  const userId = status.saxoUserId || '1015144';
  // GET /api/saxo/auth is JWT-protected. A plain <a href> navigation can't send
  // the Authorization header, so append the JWT as a ?token= query param (the
  // auth middleware accepts that fallback). Without this the reconnect link
  // 401s with "Access denied. No token provided." — the bug that blocked the
  // Saxo Mama reconnect (fixed 05-Jul-2026).
  const baseAuthUrl = status.authUrl || '/api/saxo/auth';
  const jwt = localStorage.getItem('token');
  const authUrl = jwt
    ? `${baseAuthUrl}${baseAuthUrl.includes('?') ? '&' : '?'}token=${encodeURIComponent(jwt)}`
    : baseAuthUrl;
  const errMsg = status.healthError || (status.connected ? 'Token chain invalidated' : 'Not authorized yet');

  return (
    <section
      style={{
        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
        border: '1px solid #d97706',
        borderRadius: 8,
        padding: '12px 16px',
        margin: '0 0 16px 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        flexWrap: 'wrap',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: '1 1 auto' }}>
        <span style={{ fontSize: 24 }} aria-hidden="true">⚠️</span>
        <div>
          <div style={{ fontWeight: 600, color: '#78350f', fontSize: 15 }}>
            Saxo connection broken — reconnect required
          </div>
          <div style={{ color: '#78350f', fontSize: 13, marginTop: 2 }}>
            Token chain rejected by Saxo (<code style={{ fontSize: 12 }}>{errMsg}</code>).
            {' '}Saxo card will return 401 until you re-authorize.
          </div>
          <div style={{ color: '#78350f', fontSize: 13, marginTop: 6 }}>
            Log in with user ID <strong style={{ fontFamily: 'monospace', background: '#fff7ed', padding: '1px 6px', borderRadius: 3, border: '1px solid #d97706' }}>{userId}</strong>
          </div>
        </div>
      </div>
      <a
        href={authUrl}
        rel="noopener noreferrer"
        style={{
          background: '#d97706',
          color: '#fff',
          padding: '10px 18px',
          borderRadius: 6,
          textDecoration: 'none',
          fontWeight: 600,
          fontSize: 14,
          whiteSpace: 'nowrap',
        }}
      >
        Reconnect Saxo →
      </a>
    </section>
  );
}
